import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Collapse,
  Tooltip,
  Drawer,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import lightModeIcon from "../../assets/lightmodeicon.png";
import { modalVariant } from "../Modal/Modalutils";
import { saveUserDetails, showModal } from "../../store/slices/common";
import { getSx } from "./style";
import routePaths from "../../router/route-paths";
import LogoutIcon from "../../assets/logout-icon.svg";
import { getProfile } from "../../http/auth";
import {
  deleteSessionData,
  deleteToken,
  getUserInfo,
} from "../../utils/auth-token-utils/cookie";
import { loginUser } from "../../comet-chat";
import {
  filterSidebarGroups,
  getRoleLabel,
  getUserRole,
  isManager,
} from "../../utils/roleAccess";
import { sidebarGroups } from "../../utils/sidebarItems";
import { usePermissions } from "../../hooks/usePermissions";
import { useSidebar } from "../../context/SidebarContext";

function isPathActive(pathname, itemPath) {
  if (!itemPath) return false;
  if (pathname === itemPath) return true;
  // Avoid matching /dashboard when on /dashboard-something; allow nested routes
  return pathname.startsWith(itemPath + "/");
}

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const classes = getSx();
  const user = useSelector((state) => state.common.userDetails);
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    isMobile,
    isCollapsed,
    toggleCollapse,
    sidebarWidth,
    mobileOpen,
    closeMobile,
  } = useSidebar();
  const { isAdmin, isManager: isManagerUser } = usePermissions();

  // The off-canvas drawer always shows the full (labelled) navigation; the
  // icon-only collapsed state only applies to the desktop rail.
  const collapsed = !isMobile && isCollapsed;

  // Close the drawer after any navigation (nav item, dashboard link, etc.).
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  const userRole = getUserRole(user);
  // Per-account: a restricted admin only sees the pages it was granted.
  const filteredGroups = useMemo(
    () => (user ? filterSidebarGroups(sidebarGroups, user) : []),
    [user]
  );

  const [openGroups, setOpenGroups] = useState({});

  // Auto-expand the group containing the active route
  useEffect(() => {
    const pathname = location.pathname;
    setOpenGroups((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((group) => {
        const hasActive = group.items.some((item) =>
          isPathActive(pathname, item.path)
        );
        if (hasActive) {
          next[group.id] = true;
        }
      });
      return next;
    });
  }, [location.pathname, filteredGroups]);

  useEffect(() => {
    getUserProfile();
  }, []);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const logoutHandler = () => {
    handleMenuClose();
    dispatch(
      showModal({
        modalType: modalVariant.LOGOUT,
        resolveCallback: () => {
          dispatch(showModal(null));
          deleteSessionData();
          deleteToken();
          navigate("/");
        },
        rejectCallback: () => {
          dispatch(showModal(null));
        },
      })
    );
  };

  const changePasswordHandler = () => {
    handleMenuClose();
    navigate(routePaths.CHANGE_PASSWORD);
  };

  const getUserProfile = async () => {
    try {
      const response = await getProfile();
      const userData = response.data.data?.userDetails;

      if (userData) dispatch(saveUserDetails(userData));
      const savedUser = getUserInfo();
      if (savedUser?.cometChat?.uid) await loginUser(savedUser, dispatch);
    } catch (error) {}
  };

  const renderNavIcon = (item, isActive, size = 22) => {
    const Icon = item.image;
    if (item.customIcon) {
      return (
        <img
          src={item?.whiteImage || item.image}
          alt={item.label}
          width={size}
          height={size}
          style={{
            objectFit: "contain",
            filter: "brightness(0) invert(1)",
            opacity: isActive ? 1 : 0.85,
          }}
        />
      );
    }
    return (
      <Icon
        sx={{
          fontSize: size,
          color: "#ffffff",
          opacity: isActive ? 1 : 0.85,
          filter: isActive
            ? "drop-shadow(0 0 8px rgba(255, 255, 255, 0.5))"
            : "none",
        }}
      />
    );
  };

  const renderNavItem = (item) => {
    const isActive = isPathActive(location.pathname, item.path);

    const itemContent = (
      <Box
        key={item.label}
        onClick={() => {
          navigate(item.path);
          if (isMobile) closeMobile();
        }}
        sx={{
          ...classes.sidebarItemContainer,
          background: isActive ? "rgba(255, 255, 255, 0.25)" : "transparent",
          boxShadow: isActive
            ? "0 0 20px rgba(255, 255, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
            : "none",
          justifyContent: collapsed ? "center" : "flex-start",
          // Larger touch targets inside the mobile drawer.
          padding: collapsed
            ? "10px 8px"
            : {
                xs: "12px 14px",
                md: "8px 14px",
                lg: "10px 16px",
              },
          margin: collapsed
            ? "4px 6px"
            : {
                xs: "2px 8px",
                md: "2px 8px",
              },
          gap: collapsed ? 0 : "10px",
        }}
      >
        <Box
          sx={{
            width: "28px",
            height: "28px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {renderNavIcon(item, isActive, collapsed ? 26 : 22)}
        </Box>
        {!collapsed && (
          <Typography
            variant="body1"
            sx={{
              fontSize: {
                xs: "14px",
                md: "13px",
                lg: "14px",
              },
              fontWeight: isActive ? 600 : 500,
              color: "#ffffff",
              opacity: isActive ? 1 : 0.9,
              textShadow: isActive
                ? "0 0 10px rgba(255, 255, 255, 0.3)"
                : "none",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.label}
          </Typography>
        )}
      </Box>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.label} title={item.label} placement="right" arrow>
          {itemContent}
        </Tooltip>
      );
    }

    return itemContent;
  };

  const sidebarBody = (
    <Box sx={classes.itemList}>
      {/* Logo Section */}
      <Box
        sx={{
          paddingBottom: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
          marginBottom: "8px",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          padding: "0 12px 12px",
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            overflow: "hidden",
          }}
        >
          {!collapsed && (
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: { xs: "20px", md: "20px", lg: "22px", xl: "26px" },
                color: "#ffffff",
                whiteSpace: "nowrap",
                letterSpacing: "-0.02em",
                textShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
              }}
            >
              Pagent
            </Typography>
          )}
          <img
            src={lightModeIcon}
            alt="Pagent"
            style={{
              width: collapsed ? "32px" : "28px",
              height: collapsed ? "32px" : "28px",
              objectFit: "contain",
              filter: "brightness(0) invert(1)",
            }}
          />
        </Box>
        <IconButton
          onClick={isMobile ? closeMobile : toggleCollapse}
          size="small"
          aria-label={
            isMobile
              ? "Close navigation menu"
              : collapsed
              ? "Expand sidebar"
              : "Collapse sidebar"
          }
          sx={{
            padding: isMobile ? "8px" : "4px",
            color: "#ffffff",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              transform: "scale(1.1)",
            },
          }}
        >
          {isMobile ? (
            <CloseIcon fontSize="small" />
          ) : collapsed ? (
            <MenuIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* Menu Groups */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "0 6px 100px",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.3)",
            borderRadius: "4px",
          },
        }}
      >
        {filteredGroups.map((group) => {
          const isOpen = !!openGroups[group.id];
          const groupHasActive = group.items.some((item) =>
            isPathActive(location.pathname, item.path)
          );

          // Collapsed: just show all item icons flat (no group headers)
          if (collapsed) {
            return (
              <Box key={group.id} sx={{ mb: 1 }}>
                {group.items.map(renderNavItem)}
              </Box>
            );
          }

          return (
            <Box key={group.id} sx={{ mb: 0.5 }}>
              <Box
                onClick={() => toggleGroup(group.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px 4px",
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover .group-label": {
                    opacity: 1,
                  },
                }}
              >
                <Typography
                  className="group-label"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#ffffff",
                    opacity: groupHasActive ? 1 : 0.55,
                  }}
                >
                  {group.label}
                </Typography>
                <Box
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {isOpen ? (
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  )}
                </Box>
              </Box>
              <Collapse in={isOpen} timeout="auto" unmountOnExit={false}>
                <Box>{group.items.map(renderNavItem)}</Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Profile Section */}
      <Box
        sx={{
          ...classes.profileContainer,
          justifyContent: collapsed ? "center" : "space-between",
          padding: collapsed
            ? "12px 8px"
            : {
                xs: "14px 12px",
                md: "14px 15px",
                lg: "16px 18px",
                xl: "18px 20px",
              },
        }}
      >
        {!collapsed && (
          <Box sx={classes.profileDetails}>
            <Typography sx={classes.profileName}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography sx={classes.email}>{user?.email}</Typography>
            <Typography
              sx={{
                fontSize: {
                  xs: "12px",
                  lg: "13px",
                  xl: "14px",
                },
                color: "rgba(255, 255, 255, 0.7)",
                textTransform: "capitalize",
                fontWeight: 500,
              }}
            >
              {getRoleLabel(userRole)}
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={handleMenuClick}
          aria-label="Account menu"
          sx={{
            marginLeft: collapsed ? 0 : "auto",
            padding: {
              xs: "8px",
              md: "8px",
            },
          }}
        >
          <Box
            component="img"
            src={LogoutIcon}
            alt="Logout"
            sx={{
              width: {
                xs: "20px",
                sm: "22px",
                md: "24px",
              },
              height: {
                xs: "20px",
                sm: "22px",
                md: "24px",
              },
            }}
          />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              marginLeft: isMobile ? 0 : "120px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "8px",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(102, 126, 234, 0.2)",
              border: "1px solid rgba(102, 126, 234, 0.1)",
            },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={logoutHandler}
            sx={{
              margin: "3px",
              padding: "10px 16px",
              borderRadius: "8px",
              fontWeight: 500,
              transition: "all 0.2s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)",
                color: "#ffffff",
              },
            }}
          >
            Logout
          </MenuItem>
          {(isAdmin || isManagerUser || isManager(userRole)) && (
            <MenuItem
              onClick={changePasswordHandler}
              sx={{
                margin: "3px",
                padding: "10px 16px",
                borderRadius: "8px",
                fontWeight: 500,
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#ffffff",
                },
              }}
            >
              Change Password
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );

  // Phones / small tablets: off-canvas drawer that overlays the content and
  // is opened from the hamburger in the mobile app bar.
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            ...classes.sidebarContainer,
            position: "relative",
            height: "100%",
            width: sidebarWidth,
            maxWidth: "85vw",
            borderRadius: 0,
            border: "none",
            boxShadow: "none",
          },
        }}
      >
        {sidebarBody}
      </Drawer>
    );
  }

  // Desktop: permanently visible rail that can be collapsed to icons only.
  return (
    <Box
      width={sidebarWidth}
      sx={{
        ...classes.sidebarContainer,
        transition: "width 0.3s ease",
      }}
    >
      {sidebarBody}
    </Box>
  );
}

export default Sidebar;
